import React, { ReactNode, useMemo, ForwardRefExoticComponent, forwardRef, ForwardedRef, ElementType } from 'react';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { DOMProps, HoverEvents, HoverEvent } from '@react-types/shared';
import { mergeProps, useButton, useFocusRing, useHover } from 'react-aria';

import { cn } from '../../../lib/css/className/className';
import './UserBarItem.css';
import { Stack } from '../../Stack';
import { UserBarNotification } from '../UserBarNotification';
import { ColorlessIconProps } from '../../../icons/types';
import { TestProps } from '../../../internal/types/testTypes';
import { useObjectRef } from '../../../hooks/useObjectRef';

const cnUserBarItem = cn('UserBarItem');

export interface UserBarItemProps
    extends TestProps,
        DOMProps,
        Loggable,
        Pick<HoverEvents, 'onHoverStart' | 'onHoverEnd'> {
    /**
     * иконка пункта юзербара, если он представлен одной из стандартных colorless-иконок
     * отображается в случае, когда не передан children
     */
    icon?: ForwardRefExoticComponent<ColorlessIconProps>;
    /**
     * наличие уведомлений у пункта юзербара
     */
    hasNotifications?: boolean;
    /**
     * количество уведомлений у пункта юзербара
     */
    notificationsCount?: number;
    /**
     * обработчик клика на элемент
     */
    onClick?: () => void;
    /**
     * пункт юзербара, представленный кастомным компонентом
     */
    children?: ReactNode;
    /**
     * Включен ли сейчас пункт UserBar'a
     */
    isActive?: boolean;
    /**
     * если надо явно определить ховер снаружи, по дефолту ховер обрабатывается внутри компонента
     */
    isHovered?: boolean;
    /**
     * Альтернативный тег для компонента (например, 'div')
     * @default button
     */
    as?: ElementType;
    /**
     * Дополнительный класс для компонента
     */
    className?: string;
}

/**
 * Компонент для отображения элемента UserBar'а
 */
export const UserBarItem = forwardRef<HTMLElement, UserBarItemProps>(function UserBarItem(
    props: UserBarItemProps,
    forwardedRef: ForwardedRef<HTMLElement>,
) {
    const {
        icon,
        hasNotifications,
        notificationsCount,
        onClick,
        onHoverStart,
        onHoverEnd,
        children,
        isActive,
        logNode,
        isHovered: isHoveredProp,
        as: ElementTag = 'button',
        className,
        'data-testid': dataTestId,
    } = props;
    const ref = useObjectRef(forwardedRef);
    const { LogProvider, logClick } = useLogger('UserBarItem', logNode);
    const { hoverProps, isHovered } = useHover({
        onHoverStart: (event: HoverEvent) => onHoverStart?.(event),
        onHoverEnd: (event: HoverEvent) => onHoverEnd?.(event),
    });
    const { focusProps, isFocusVisible } = useFocusRing();
    const { buttonProps } = useButton(
        {
            onPress: () => {
                logClick?.();
                onClick?.();
            },
            elementType: ElementTag,
        },
        ref,
    );

    const CurrentIcon = useMemo(() => {
        if (!icon) return null;

        const Icon = icon;
        return <Icon role={isActive ? 'main' : 'supplementary'} size="24" color="gray" />;
    }, [icon, isActive]);

    const content = useMemo(() => {
        const iconElement = hasNotifications ? (
            <UserBarNotification data-testid={dataTestId} counter={notificationsCount}>
                {CurrentIcon}
            </UserBarNotification>
        ) : (
            CurrentIcon
        );

        return (
            <Stack alignItems={'center'} justifyContent="center" className={cnUserBarItem('icon')}>
                {children ?? iconElement}
            </Stack>
        );
    }, [hasNotifications, dataTestId, notificationsCount, CurrentIcon, children]);

    return (
        <LogProvider>
            <ElementTag
                ref={ref}
                {...mergeProps(hoverProps, buttonProps, focusProps)}
                className={cnUserBarItem(
                    '',
                    {
                        active: isActive,
                        hovered: isHovered || isHoveredProp,
                        focused: isFocusVisible,
                    },
                    [className],
                )}
                data-testid={props['data-testid'] || 'UserBar.UserBarItem'}
            >
                {content}
            </ElementTag>
        </LogProvider>
    );
});
